import { ColumnDef, CellContext } from '@tanstack/react-table'
// import { type TableColumn, type RowAction, TableSchema } from './types'
// import { TableSchema } from './types'
// import { TableSchema } from './types-v2'
// import { TableSchema } from './types-v3'
import { Button } from '@/components/shadcn/button'
import { ComposedSheet } from '../../../../components/ui/ComposedSheet'
import { Badge } from '@/components/shadcn/badge'
import { TableSchema, TableColumn, MetadataFieldConfig } from './types-v4'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { FormComponent } from '../FormComponent/FormComponent'
import { InferFormValues } from '../FormComponent/types'
import { tableToFormSchema } from '../../helpers'
import { SheetWithForm } from '@/components/ui/SheetWithForm'
import { IconEditOutline } from '@/components/icons'
import { formatDate } from 'date-fns'
// import { tableToFormSchema } from '../../helpers'
// import { FormComponent, InferFormValues } from '../FormComponent/FormComponent'

// This type is used to define the shape of our data.
// You can use a Zod schema here if you want.
// export type Payment = {
//   id: string
//   amount: number
//   status: "pending" | "processing" | "success" | "failed"
//   email: string
// }

const getMetadataForValue = (value: unknown, metadataFields: MetadataFieldConfig[]) => {
  if (!metadataFields?.length || value === undefined || value === null) {
    return {}
  }

  // Create a metadata object based on the field mappings
  const metadata: Record<string, string> = {}
  metadataFields.forEach((field) => {
    if (field.mapping && field.mapping[value as string]) {
      metadata[field.property] = field.mapping[value as string]
    }
  })

  return metadata
}

// Sample helper to get the appropriate cell renderer
const getCellRenderer = (column: TableColumn, value: unknown) => {
  if (value === null || value === undefined) return null
  // For Badge components with metadata
  if (column.columnConfig.display?.component === 'Badge') {
    if (column.columnConfig.editing?.type === 'array') {
      return (
        <div className="flex flex-wrap gap-1">
          {(value as string[]).map((item) => (
            <Badge key={item} variant="outline" className="whitespace-nowrap bg-muted text-muted-foreground shadow">
              {item}
            </Badge>
          ))}
        </div>
      )
    }
    if (column.columnConfig.metadata?.fields) {
      // if (value === null || value === undefined) return null
      // Get metadata based on schema definition
      const metadata = getMetadataForValue(value, column.columnConfig.metadata.fields)
      return (
        <Badge className="whitespace-nowrap" style={{ backgroundColor: metadata.color }}>
          {value as string}
        </Badge>
      )
    }
  }
  if (column.columnConfig.editing?.type === 'array') {
    return (value as string[]).join(', ')
  }
  // Handle different value types appropriately
  // if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  if (column.columnConfig.display?.icon) {
    return (
      <div className="flex items-center gap-2">
        <DynamicIcon size={16} iconName={column.columnConfig.display?.icon} />
        {value as string}
      </div>
    )
  }
  if (
    column.columnConfig.editing?.type === 'string' &&
    ['date', 'datetime', 'date-time'].includes(column.columnConfig.editing.type)
  ) {
    if (column.columnConfig.display?.formatter) {
      return formatDate(new Date(value as string), column.columnConfig.display.formatter)
    }
    return formatDate(new Date(value as string), 'MM/dd/yyyy')
  }
  return value
}

export function getColumns<T>(tableSchema: TableSchema): ColumnDef<T>[] {
  const accessorColumns = tableSchema.columns
    .filter((col) => col.columnConfig.visibility.visible)
    .map((col) => {
      return {
        id: col.id,
        accessorKey: col.id,
        header: col.title,
        // Header: col.title,
        // cell: (info: CellContext<T, unknown>) =>
        //   col.columnConfig.display?.component ? `Comp: "${col.columnConfig.display.component}"` : info.getValue(),
        cell: (info: CellContext<T, unknown>) => {
          const value = info.getValue()
          return getCellRenderer(col, value)
        },
        // width: col.columnConfig.display?.width || 150,
      }
    })

  const rowActionColumn = tableSchema.actions.row.length
    ? {
        id: 'rowActions',
        cell: (info: CellContext<T, unknown>) =>
          tableSchema.actions.row.map((action) => {
            if (tableSchema.tableConfig.permissions.canEdit && action.id === 'edit') {
              const existingEntry = { ...info.row.original }
              const formSchema = tableToFormSchema(tableSchema, existingEntry as Record<string, unknown>)
              return (
                <SheetWithForm
                  key={action.id}
                  triggerTooltip={action.label}
                  trigger={
                    <Button variant="ghost" size="icon" className="size-4">
                      <DynamicIcon
                        iconName={action.icon}
                        lazyLoad={false}
                        className="size-3"
                        fallback={<IconEditOutline className="size-3" />}
                      />
                      <span className="sr-only">{action.label}</span>
                    </Button>
                  }
                  schema={formSchema}
                  onSubmit={(data: InferFormValues<typeof formSchema>) => {
                    console.log(data)
                  }}
                />
              )
            }
            // Also add case for delete here
            return null
          }),
      }
    : undefined

  if (rowActionColumn) {
    return [...accessorColumns, rowActionColumn]
  }

  return [...accessorColumns]
}
